import * as React from 'react';

import isEqual from 'lodash/isEqual';

import {SaveButton} from 'shells/button/save-button/save-button';
import {Stack} from 'shells/layout/stack/stack';

import {Button} from '../button/button';
import {Modal} from '../modal/modal';
import type {FooterButtonProps} from '../select-picker-generic/select-picker-generic';
import type {TwoLineOptionWithIcon} from '../select-two-line-icon/select-two-line-icon';
import {Body} from '../typography/body';

import {SelectionDropdownAndList} from './selection-dropdown-and-list';

type SharedButtonConfig = {
    label?: string;
};

type NormalButtonConfig = SharedButtonConfig & {
    type: 'normal';
    onSelection: (selected: TwoLineOptionWithIcon<any>[]) => void;
};

type SaveButtonConfig = SharedButtonConfig & {
    type: 'save';
    savingText?: string;
    successText?: string;
    onSelection: (selected: TwoLineOptionWithIcon<any>[]) => Promise<void>;
};

type ButtonConfig = NormalButtonConfig | SaveButtonConfig;

type Props = {
    title: string;
    subtitle?: string;
    isOpen: boolean;
    selectPlaceholder: string;
    selectFooterButton?: FooterButtonProps;
    options: TwoLineOptionWithIcon<any>[];
    selectedOptions?: TwoLineOptionWithIcon<any>[];
    nothingFoundState: string;
    buttonConfig: ButtonConfig;
    onClose: () => void;
};

export function SelectionModal(props: Props) {
    const [selectedOptions, setSelectedOptions] = React.useState<TwoLineOptionWithIcon<any>[]>(
        props.selectedOptions || []
    );
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorSaving, setErrorSaving] = React.useState<boolean>(false);

    const remainingOptions = props.options.filter(
        (option) => !selectedOptions.find((selectedOption) => selectedOption.value === option.value)
    );

    const existingSelection = props.selectedOptions
        ? props.selectedOptions.map((option) => option.value)
        : [];
    const currentSelection = selectedOptions.map((option) => option.value);
    const hasMadeChanges = !isEqual(existingSelection, currentSelection);

    const buttonConfig = props.buttonConfig;

    return (
        <Modal
            isOpen={props.isOpen}
            closeButton={false}
            noPadding={true}
            maxWidth={452}
            fitHeight={true}
            onClose={props.onClose}
        >
            <div className='py-24 px-16 full-height'>
                <Stack spacing={16} isFullHeight={true}>
                    <Body size='xlarge' isBold={true}>
                        {props.title}
                    </Body>
                    <SelectionDropdownAndList
                        remainingOptions={remainingOptions}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        selectPlaceholder={props.selectPlaceholder}
                        selectFooterButton={props.selectFooterButton}
                        subtitle={props.subtitle}
                        nothingFoundState={props.nothingFoundState}
                    />
                    <Stack
                        isHorizontal={true}
                        justifyContent='space-between'
                        alignItems='center'
                        isFullWidth={true}
                        spacing={60}
                    >
                        <Body color='error' size='small'>
                            {errorSaving ? 'Something went wrong. Please try again.' : undefined}
                        </Body>
                        <div className='flex gap-16 justify-end'>
                            <Button onClick={props.onClose}>Cancel</Button>
                            {isNormalButtonConfig(buttonConfig) && (
                                <Button
                                    variant='save'
                                    onClick={() => {
                                        props.buttonConfig.onSelection(selectedOptions);
                                    }}
                                    disabled={!hasMadeChanges}
                                >
                                    {props.buttonConfig.label || 'Confirm changes'}
                                </Button>
                            )}
                            {isSaveButtonConfig(buttonConfig) && (
                                <div className='text-nowrap'>
                                    <SaveButton
                                        isSaving={isSaving}
                                        size='big'
                                        errorSaving={errorSaving}
                                        disabled={!hasMadeChanges}
                                        onClick={async () => {
                                            setErrorSaving(false);
                                            setIsSaving(true);

                                            return buttonConfig
                                                .onSelection(selectedOptions)
                                                .catch(() => {
                                                    setErrorSaving(true);
                                                })
                                                .finally(() => {
                                                    setIsSaving(false);
                                                });
                                        }}
                                        buttonText={buttonConfig.label || 'Confirm changes'}
                                        savingText={
                                            buttonConfig.savingText || 'Confirming changes...'
                                        }
                                        successText={
                                            buttonConfig.successText || 'Changes confirmed...'
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </Stack>
                </Stack>
            </div>
        </Modal>
    );
}

function isNormalButtonConfig(buttonConfig: ButtonConfig): buttonConfig is NormalButtonConfig {
    return buttonConfig.type === 'normal';
}

function isSaveButtonConfig(buttonConfig: ButtonConfig): buttonConfig is SaveButtonConfig {
    return buttonConfig.type === 'save';
}
